import React from "react";

import { useAuthDetails } from "@/context/auth-context";
import { useDebugSettings } from "@/hooks/use-debug-settings";
import { cn } from "@/lib/utils";

type GateProps = {
  name: string;
  description: string;
  value?: string;
  equals?: string | string[];
  notEquals?: string | string[];
  children: React.ReactElement;
  debug?: boolean;
  disableImmunity?: boolean;
};

export const Gate: React.FC<GateProps> = ({
  name,
  description,
  value,
  equals,
  notEquals,
  debug,
  children,
  disableImmunity = false,
}) => {
  const authDetails = useAuthDetails();
  const { overlay: overlaySettings } = useDebugSettings();

  const equalsArray = Array.isArray(equals) ? equals : [equals];
  const notEqualsArray = Array.isArray(notEquals) ? notEquals : [notEquals];

  const showFeature =
    value === undefined ||
    (equals !== undefined && equalsArray.includes(value)) ||
    (notEquals !== undefined && !notEqualsArray.includes(value));
  const showOverlay =
    overlaySettings &&
    (disableImmunity ? showFeature : authDetails?.role === "super");

  if (debug && showOverlay === false) {
    return null;
  }

  if (showOverlay === false) {
    if (showFeature) return children;
    return;
  }

  if (!React.isValidElement(children)) {
    console.error("Gate can have only one child.");
    return <div className="text-foreground bg-red-500 px-1 py-0.5">Error!</div>;
  }

  const containerClassName = cn(
    "before:content-[''] before:absolute before:z-1001 ",
    "before:inset-0 before:border-[1px] before:bg-opacity-20 ",
    "before:pointer-events-none",
    "hover:before:opacity-0 before:transition-opacity before:duration-200",
    {
      "before:border-yellow-500 before:bg-yellow-500/10": debug,
      "before:border-red-500 before:bg-red-500/10": !debug && !showFeature,
      "before:border-green-500 before:bg-green-500/10": !debug && showFeature,
    },
  );

  const labelClassName = cn(
    "after:content-[attr(data-gate-name)] after:absolute after:z-1002",
    "after:absolute after:top-0 after:left-0",
    "after:px-1 after:py-0.5 after:bg-opacity-20 after:text-[10px] after:text-white after:max-w-full after:break-words",
    "after:pointer-events-none",
    "hover:after:opacity-0 after:transition-opacity after:duration-200",
    {
      "after:bg-yellow-500": debug,
      "after:bg-red-500": !debug && !showFeature,
      "after:bg-green-500": !debug && showFeature,
    },
  );

  const props = {
    ...(children.props as React.PropsWithChildren & {
      className?: string;
      onMouseOver?: (e: React.MouseEvent) => void;
    }),
  };

  // Check if the child element already has absolute positioning
  const hasAbsolutePositioning = props.className?.includes("absolute") === true;

  return React.cloneElement(children, {
    ...props,

    // @ts-ignore
    className: cn(props.className, containerClassName, labelClassName, {
      relative: !hasAbsolutePositioning,
    }),
    "data-gate-name": `${name}: ${equals ? equalsArray.join(", ") : notEqualsArray.join(", ")}${debug ? " (debug)" : ""}`,

    onMouseOver: (e: React.MouseEvent) => {
      let log = `${name}
${description}

Display children: ${showFeature ? "🟩 Yes" : "🟥 No"}
Value: ${value}
Debug: ${debug ? "⚠️ Yes" : "No"}
`;
      if (equals !== undefined)
        log += `Expected value: ${equalsArray.join(", ")}`;
      if (notEquals !== undefined)
        log += `Not expected value: ${notEqualsArray.join(", ")}`;
      console.log(log);

      props.onMouseOver?.(e);
    },
  });
};
